import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  addDoc,
  Firestore,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification, deleteUser, getAuth, User as FirebaseUser } from 'firebase/auth';
import { User, UserRole, UserPermissions } from '../utils/types';
import { db } from '../config/firebase';
import { getWarehouseById } from './inventoryService';
import { getBranchById } from './branchService';

/**
 * الحصول على الصلاحيات الافتراضية حسب دور المستخدم
 * @param role دور المستخدم
 * @returns الصلاحيات الافتراضية
 */
export const getDefaultPermissions = (role: UserRole): UserPermissions => {
  // الصلاحيات الافتراضية لمدير النظام
  if (role === UserRole.ADMIN) {
    return {
      // صلاحيات الوصول للصفحات
      canAccessDashboard: true,
      canAccessInventory: true,
      canAccessPOS: true,
      canAccessMaintenance: true,
      canAccessCustomers: true,
      canAccessSettings: true,
      canAccessReports: true,
      canModifyInventory: true,
      canAccessDebts: true,
      canViewExpenses: true,
      canViewBasicExpenses: true,
      canViewDailyExpenses: true,
      canAddExpenses: true,
      canEditExpenses: true,
      canDeleteExpenses: true,
      canManageBackup: true,

      // صلاحيات المخزون
      canViewInventory: true,
      canAddInventory: true,
      canEditInventory: true,
      canDeleteInventory: true,
      canTransferInventory: true,

      // صلاحيات المبيعات
      canViewSales: true,
      canCreateSales: true,
      canEditSales: true,
      canDeleteSales: true,
      canApplyDiscount: true,

      // صلاحيات الصيانة
      canViewMaintenance: true,
      canCreateMaintenance: true,
      canEditMaintenance: true,
      canDeleteMaintenance: true,
      canAssignTechnician: true,
      canChangeMaintStatus: true,

      // صلاحيات العملاء
      canViewCustomers: true,
      canAddCustomers: true,
      canEditCustomers: true,
      canDeleteCustomers: true,

      // صلاحيات الفروع والمخازن
      canManageBranches: true,
      canManageWarehouses: true,

      // صلاحيات المستخدمين
      canManageUsers: true,
      canViewUsers: true,

      // صلاحيات الإعدادات
      canManageSettings: true
    };
  }

  // الصلاحيات الافتراضية لمدير الفرع
  if (role === UserRole.BRANCH_MANAGER) {
    return {
      // صلاحيات الوصول للصفحات
      canAccessDashboard: true,
      canAccessInventory: true,
      canAccessPOS: true,
      canAccessMaintenance: true,
      canAccessCustomers: true,
      canAccessSettings: true,
      canAccessReports: true,
      canModifyInventory: true,
      canAccessDebts: true,
      canViewExpenses: true,
      canViewBasicExpenses: true,
      canViewDailyExpenses: true,
      canAddExpenses: true,
      canEditExpenses: true,
      canDeleteExpenses: true,
      canManageBackup: false,

      // صلاحيات المخزون
      canViewInventory: true,
      canAddInventory: true,
      canEditInventory: true,
      canDeleteInventory: true,
      canTransferInventory: true,

      // صلاحيات المبيعات
      canViewSales: true,
      canCreateSales: true,
      canEditSales: true,
      canDeleteSales: true,
      canApplyDiscount: true,

      // صلاحيات الصيانة
      canViewMaintenance: true,
      canCreateMaintenance: true,
      canEditMaintenance: true,
      canDeleteMaintenance: true,
      canAssignTechnician: true,
      canChangeMaintStatus: true,

      // صلاحيات العملاء
      canViewCustomers: true,
      canAddCustomers: true,
      canEditCustomers: true,
      canDeleteCustomers: true,

      // صلاحيات الفروع والمخازن
      canManageBranches: false,
      canManageWarehouses: true,

      // صلاحيات المستخدمين
      canManageUsers: false,
      canViewUsers: true,

      // صلاحيات الإعدادات
      canManageSettings: false
    };
  }

  // الصلاحيات الافتراضية لمدير المخزن
  if (role === UserRole.WAREHOUSE_MANAGER) {
    return {
      // صلاحيات الوصول للصفحات
      canAccessDashboard: true,
      canAccessInventory: true,
      canAccessPOS: true,
      canAccessMaintenance: false,
      canAccessCustomers: false,
      canAccessSettings: false,
      canAccessReports: true,
      canModifyInventory: true,
      canAccessDebts: false,
      canViewExpenses: false,
      canViewBasicExpenses: true,
      canViewDailyExpenses: true,
      canAddExpenses: false,
      canEditExpenses: false,
      canDeleteExpenses: false,
      canManageBackup: false,

      // صلاحيات المخزون
      canViewInventory: true,
      canAddInventory: true,
      canEditInventory: true,
      canDeleteInventory: false,
      canTransferInventory: true,

      // صلاحيات المبيعات
      canViewSales: true,
      canCreateSales: true,
      canEditSales: true,
      canDeleteSales: false,
      canApplyDiscount: false,

      // صلاحيات الصيانة
      canViewMaintenance: false,
      canCreateMaintenance: false,
      canEditMaintenance: false,
      canDeleteMaintenance: false,
      canAssignTechnician: false,
      canChangeMaintStatus: false,

      // صلاحيات العملاء
      canViewCustomers: true,
      canAddCustomers: false,
      canEditCustomers: false,
      canDeleteCustomers: false,

      // صلاحيات الفروع والمخازن
      canManageBranches: false,
      canManageWarehouses: false,

      // صلاحيات المستخدمين
      canManageUsers: false,
      canViewUsers: false,

      // صلاحيات الإعدادات
      canManageSettings: false
    };
  }

  // الصلاحيات الافتراضية لفني الصيانة
  if (role === UserRole.TECHNICIAN) {
    return {
      // صلاحيات الوصول للصفحات
      canAccessDashboard: true,
      canAccessInventory: false,
      canAccessPOS: false,
      canAccessMaintenance: true,
      canAccessCustomers: true,
      canAccessSettings: false,
      canAccessReports: false,
      canModifyInventory: false,
      canAccessDebts: false,
      canViewExpenses: false,
      canViewBasicExpenses: false,
      canViewDailyExpenses: true,
      canAddExpenses: false,
      canEditExpenses: false,
      canDeleteExpenses: false,
      canManageBackup: false,

      // صلاحيات المخزون
      canViewInventory: false,
      canAddInventory: false,
      canEditInventory: false,
      canDeleteInventory: false,
      canTransferInventory: false,

      // صلاحيات المبيعات
      canViewSales: false,
      canCreateSales: false,
      canEditSales: false,
      canDeleteSales: false,
      canApplyDiscount: false,

      // صلاحيات الصيانة
      canViewMaintenance: true,
      canCreateMaintenance: true,
      canEditMaintenance: true,
      canDeleteMaintenance: false,
      canAssignTechnician: false,
      canChangeMaintStatus: true,

      // صلاحيات العملاء
      canViewCustomers: true,
      canAddCustomers: true,
      canEditCustomers: true,
      canDeleteCustomers: false,

      // صلاحيات الفروع والمخازن
      canManageBranches: false,
      canManageWarehouses: false,

      // صلاحيات المستخدمين
      canManageUsers: false,
      canViewUsers: false,

      // صلاحيات الإعدادات
      canManageSettings: false
    };
  }

  // الصلاحيات الافتراضية للمستخدم العادي
  return {
    // صلاحيات الوصول للصفحات
    canAccessDashboard: true,
    canAccessInventory: false,
    canAccessPOS: false,
    canAccessMaintenance: false,
    canAccessCustomers: false,
    canAccessSettings: false,
    canAccessReports: false,
    canModifyInventory: false,
    canAccessDebts: false,
    canViewExpenses: false,
    canViewBasicExpenses: false,
    canViewDailyExpenses: false,
    canAddExpenses: false,
    canEditExpenses: false,
    canDeleteExpenses: false,
    canManageBackup: false,

    // صلاحيات المخزون
    canViewInventory: false,
    canAddInventory: false,
    canEditInventory: false,
    canDeleteInventory: false,
    canTransferInventory: false,

    // صلاحيات المبيعات
    canViewSales: false,
    canCreateSales: false,
    canEditSales: false,
    canDeleteSales: false,
    canApplyDiscount: false,

    // صلاحيات الصيانة
    canViewMaintenance: false,
    canCreateMaintenance: false,
    canEditMaintenance: false,
    canDeleteMaintenance: false,
    canAssignTechnician: false,
    canChangeMaintStatus: false,

    // صلاحيات العملاء
    canViewCustomers: false,
    canAddCustomers: false,
    canEditCustomers: false,
    canDeleteCustomers: false,

    // صلاحيات الفروع والمخازن
    canManageBranches: false,
    canManageWarehouses: false,

    // صلاحيات المستخدمين
    canManageUsers: false,
    canViewUsers: false,

    // صلاحيات الإعدادات
    canManageSettings: false
  };
};

/**
 * الحصول على مستخدم بواسطة البريد الإلكتروني
 * @param email البريد الإلكتروني
 * @returns معلومات المستخدم
 */
export const getUserByEmail = async (email: string): Promise<User | null> => {
  try {
    const usersCollection = getUsersCollection();
    const q = query(usersCollection, where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data() as User;
    return {
      ...userData,
      id: userDoc.id
    };
  } catch (error) {
    console.error('Error getting user by email:', error);
    return null;
  }
};

// ضمان وجود مثيل Firestore
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('Firestore غير متوفر. تأكد من تهيئة Firebase بشكل صحيح.');
  }
  return db;
};

// الحصول على مرجع مجموعة المستخدمين
const getUsersCollection = () => {
  return collection(ensureFirestore(), 'users');
};

/**
 * الحصول على مستخدم بواسطة المعرف
 * @param userId معرف المستخدم
 * @returns معلومات المستخدم
 */
export const getUserById = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(ensureFirestore(), 'users', userId));

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data() as User;
    return {
      ...userData,
      id: userDoc.id
    };
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

/**
 * تحديث معلومات مستخدم
 * @param userId معرف المستخدم
 * @param updates التحديثات
 */
export const updateUser = async (userId: string, updates: Partial<User>): Promise<void> => {
  try {
    console.log(`Updating user ${userId} with data:`, updates);

    // التحقق من وجود المستخدم أولاً
    const userDoc = await getDoc(doc(ensureFirestore(), 'users', userId));

    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    // إعداد البيانات للتحديث
    const updateData: any = {
      ...updates,
      updatedAt: new Date()
    };

    // التأكد من أن الصلاحيات موجودة في التحديثات
    if (updates.permissions) {
      console.log(`Updating permissions for user ${userId}:`, updates.permissions);
    }

    const userRef = doc(ensureFirestore(), 'users', userId);
    await updateDoc(userRef, updateData);
    console.log(`Successfully updated user ${userId}`);
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * الحصول على جميع المستخدمين
 * @param includeInactive تضمين المستخدمين غير النشطين
 * @returns قائمة المستخدمين
 */
export const getAllUsers = async (includeInactive: boolean = false): Promise<User[]> => {
  try {
    const usersCollection = getUsersCollection();
    let q = query(usersCollection);

    if (!includeInactive) {
      q = query(q, where('isActive', '==', true));
    }

    const querySnapshot = await getDocs(q);
    const users: User[] = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data() as User;
      users.push({
        ...userData,
        id: doc.id
      });
    });

    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    return [];
  }
};

/**
 * إنشاء مستخدم جديد
 * @param email البريد الإلكتروني
 * @param password كلمة المرور
 * @param name الاسم
 * @param role الدور
 * @param branchId معرف الفرع (اختياري)
 * @param allowedWarehouses المخازن المسموح بها (اختياري)
 * @param permissions الصلاحيات (اختياري)
 * @returns معرف المستخدم الجديد
 */
export const createUser = async (
  email: string,
  password: string,
  name: string,
  role: UserRole,
  branchId?: string,
  allowedWarehouses?: string[],
  permissions?: UserPermissions
): Promise<string> => {
  try {
    const auth = getAuth();

    // إنشاء المستخدم في Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // تحديث الملف الشخصي للمستخدم
    await updateProfile(firebaseUser, {
      displayName: name
    });

    // إرسال بريد إلكتروني لتأكيد الحساب
    await sendEmailVerification(firebaseUser);

    // الحصول على اسم الفرع إذا تم تحديده
    let branchName = '';
    if (branchId) {
      const branch = await getBranchById(branchId);
      if (branch) {
        branchName = branch.name;
      }
    }

    // إنشاء مستخدم في Firestore
    const userData: User = {
      id: firebaseUser.uid,
      name,
      email,
      role,
      branchId: branchId || '',
      branchName: branchName || '',
      allowedWarehouses: allowedWarehouses || [],
      permissions: permissions || getDefaultPermissions(role),
      isActive: false, // المستخدم غير نشط حتى يتم تفعيله من قبل مدير النظام
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(ensureFirestore(), 'users', firebaseUser.uid), userData);

    return firebaseUser.uid;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * حذف مستخدم
 * @param userId معرف المستخدم
 */
export const deleteUserById = async (userId: string): Promise<void> => {
  try {
    // حذف المستخدم من Firestore
    const userRef = doc(ensureFirestore(), 'users', userId);
    await deleteDoc(userRef);

    // ملاحظة: حذف المستخدم من Firebase Authentication يتطلب صلاحيات إدارية
    // ويجب أن يتم من خلال Firebase Admin SDK في الخلفية
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * تفعيل أو تعطيل مستخدم
 * @param userId معرف المستخدم
 * @param isActive حالة التفعيل
 */
export const setUserActive = async (userId: string, isActive: boolean): Promise<void> => {
  try {
    const userRef = doc(ensureFirestore(), 'users', userId);
    await updateDoc(userRef, {
      isActive,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error setting user active status:', error);
    throw error;
  }
};

/**
 * تحديث صلاحيات المستخدم
 * @param userId معرف المستخدم
 * @param permissions الصلاحيات الجديدة
 */
export const updateUserPermissions = async (userId: string, permissions: UserPermissions): Promise<void> => {
  try {
    console.log(`Updating permissions for user ${userId}:`, permissions);

    // التحقق من وجود المستخدم أولاً
    const userDoc = await getDoc(doc(ensureFirestore(), 'users', userId));

    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const userRef = doc(ensureFirestore(), 'users', userId);
    await updateDoc(userRef, {
      permissions,
      updatedAt: new Date()
    });

    console.log(`Successfully updated permissions for user ${userId}`);
  } catch (error) {
    console.error('Error updating user permissions:', error);
    throw error;
  }
};

/**
 * تحديث المخازن المسموح بها للمستخدم
 * @param userId معرف المستخدم
 * @param warehouseIds معرفات المخازن
 */
export const updateUserWarehouses = async (userId: string, warehouseIds: string[]): Promise<void> => {
  try {
    const userRef = doc(ensureFirestore(), 'users', userId);
    await updateDoc(userRef, {
      allowedWarehouses: warehouseIds,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating user warehouses:', error);
    throw error;
  }
};

/**
 * الحصول على المستخدمين حسب الدور
 * @param role الدور
 * @param includeInactive تضمين المستخدمين غير النشطين
 * @returns قائمة المستخدمين
 */
export const getUsersByRole = async (role: UserRole, includeInactive: boolean = false): Promise<User[]> => {
  try {
    const usersCollection = getUsersCollection();
    let q = query(usersCollection, where('role', '==', role));

    if (!includeInactive) {
      q = query(q, where('isActive', '==', true));
    }

    const querySnapshot = await getDocs(q);
    const users: User[] = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data() as User;
      users.push({
        ...userData,
        id: doc.id
      });
    });

    return users;
  } catch (error) {
    console.error('Error getting users by role:', error);
    return [];
  }
};

/**
 * الحصول على المستخدمين حسب الفرع
 * @param branchId معرف الفرع
 * @param includeInactive تضمين المستخدمين غير النشطين
 * @returns قائمة المستخدمين
 */
export const getUsersByBranch = async (branchId: string, includeInactive: boolean = false): Promise<User[]> => {
  try {
    const usersCollection = getUsersCollection();
    let q = query(usersCollection, where('branchId', '==', branchId));

    if (!includeInactive) {
      q = query(q, where('isActive', '==', true));
    }

    const querySnapshot = await getDocs(q);
    const users: User[] = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data() as User;
      users.push({
        ...userData,
        id: doc.id
      });
    });

    return users;
  } catch (error) {
    console.error('Error getting users by branch:', error);
    return [];
  }
};

/**
 * الحصول على المستخدمين الذين لديهم صلاحية الوصول إلى مخزن محدد
 * @param warehouseId معرف المخزن
 * @param includeInactive تضمين المستخدمين غير النشطين
 * @returns قائمة المستخدمين
 */
export const getUsersByWarehouse = async (warehouseId: string, includeInactive: boolean = false): Promise<User[]> => {
  try {
    const usersCollection = getUsersCollection();
    let q = query(usersCollection, where('allowedWarehouses', 'array-contains', warehouseId));

    if (!includeInactive) {
      q = query(q, where('isActive', '==', true));
    }

    const querySnapshot = await getDocs(q);
    const users: User[] = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data() as User;
      users.push({
        ...userData,
        id: doc.id
      });
    });

    return users;
  } catch (error) {
    console.error('Error getting users by warehouse:', error);
    return [];
  }
};

/**
 * التحقق من صلاحية المستخدم للوصول إلى مخزن محدد
 * @param userId معرف المستخدم
 * @param warehouseId معرف المخزن
 * @returns هل لديه صلاحية الوصول
 */
export const canUserAccessWarehouse = async (userId: string, warehouseId: string): Promise<boolean> => {
  try {
    const user = await getUserById(userId);

    if (!user) {
      return false;
    }

    // مدير النظام لديه صلاحية الوصول إلى جميع المخازن
    if (user.role === UserRole.ADMIN) {
      return true;
    }

    // التحقق من المخازن المسموح بها
    if (user.allowedWarehouses && user.allowedWarehouses.includes(warehouseId)) {
      return true;
    }

    // التحقق من الفرع المرتبط بالمخزن
    if (user.branchId) {
      const warehouse = await getWarehouseById(warehouseId);
      if (warehouse && warehouse.branchId === user.branchId) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking user warehouse access:', error);
    return false;
  }
};

/**
 * التحقق من صلاحية المستخدم للوصول إلى فرع محدد
 * @param userId معرف المستخدم
 * @param branchId معرف الفرع
 * @returns هل لديه صلاحية الوصول
 */
export const canUserAccessBranch = async (userId: string, branchId: string): Promise<boolean> => {
  try {
    const user = await getUserById(userId);

    if (!user) {
      return false;
    }

    // مدير النظام لديه صلاحية الوصول إلى جميع الفروع
    if (user.role === UserRole.ADMIN) {
      return true;
    }

    // التحقق من الفرع المرتبط بالمستخدم
    if (user.branchId === branchId) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking user branch access:', error);
    return false;
  }
};

export const addUser = async (userData: {
  name: string;
  email: string;
  role: UserRole;
  branchId: string;
  branchName?: string;
  allowedWarehouses?: string[];
  permissions?: UserPermissions;
}): Promise<User> => {
  try {
    const { name, email, role, branchId, branchName, allowedWarehouses, permissions } = userData;

    // التحقق من عدم وجود مستخدم بنفس البريد الإلكتروني
    const existingUser = await getUserByEmail(email);
    if (existingUser) {
      throw new Error('البريد الإلكتروني مستخدم بالفعل');
    }

    // إنشاء مستخدم جديد
    const newUser: User = {
      id: '', // سيتم تعيينه من قبل Firestore
      name,
      email,
      role,
      branchId: branchId || '',
      branchName: branchName || '',
      allowedWarehouses: allowedWarehouses || [],
      permissions: permissions || getDefaultPermissions(role),
      isActive: false, // المستخدم غير نشط حتى يتم تفعيله من قبل مدير النظام
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // إضافة المستخدم إلى Firestore
    const userRef = await addDoc(collection(db, 'users'), newUser);
    newUser.id = userRef.id;

    return newUser;
  } catch (error) {
    console.error('Error adding user:', error);
    throw new Error('فشل في إضافة المستخدم');
  }
};

/**
 * تحديث المخازن المسموح بها للمستخدم بناءً على فرعه
 * @param userId معرف المستخدم
 */
export const updateUserWarehousesFromBranch = async (userId: string): Promise<void> => {
  try {
    const user = await getUserById(userId);
    if (!user || !user.branchId) {
      console.log('User or branchId not found');
      return;
    }

    // الحصول على الفرع
    const branch = await getBranchById(user.branchId);
    if (!branch) {
      console.log('Branch not found');
      return;
    }

    // تحديث المخازن المسموح بها
    await updateUserWarehouses(userId, branch.warehouseIds || []);
    console.log(`Updated allowedWarehouses for user ${user.name} to:`, branch.warehouseIds);
  } catch (error) {
    console.error('Error updating user warehouses from branch:', error);
  }
};

export default {
  getUserById,
  updateUser,
  getAllUsers,
  createUser,
  deleteUserById,
  setUserActive,
  updateUserPermissions,
  updateUserWarehouses,
  getUsersByRole,
  getUsersByBranch,
  getUsersByWarehouse,
  canUserAccessWarehouse,
  canUserAccessBranch
};